/**
 * CancelCampaignModal — confirmation dialog for the irreversible cancel action.
 *
 * Shared molecule consumed by both the View and List campaign pages. The
 * modal is a controlled component:
 *   - parent owns `visible`, `cancelling`, and `error` state via `useCancelCampaign`
 *   - parent fires the API call inside `onConfirm` and closes via `onDismiss`
 *
 * The modal does NOT call the API itself — keeping it pure makes it easy to
 * reuse across list and view pages and trivial to test.
 */

import { FC } from 'react';
import Modal from '@amzn/awsui-components-core/modal';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Alert from '@amzn/awsui-components-core/alert';
import { useTranslate } from 'src/hooks/useTranslate';

const CANCEL_CAMPAIGN_MODAL_BUNDLE = 'molecules.cancelCampaignModal';

export interface CancelCampaignModalProps {
    /** Whether the modal is visible. */
    visible: boolean;
    /** Fired when the user clicks "Go Back" or dismisses the modal. */
    onDismiss: () => void;
    /** Fired when the user confirms cancellation. */
    onConfirm: () => void;
    /** Identifier of the campaign being cancelled — shown in the body. */
    campaignId: string;
    /** Title of the campaign being cancelled — interpolated into the warning. */
    campaignTitle: string;
    /** True while the cancel API call is in flight. */
    cancelling: boolean;
    /** Optional translated error message — when set, shown as an inline Alert. */
    error?: string | null;
}

const CancelCampaignModal: FC<CancelCampaignModalProps> = ({
    visible,
    onDismiss,
    onConfirm,
    campaignId,
    campaignTitle,
    cancelling,
    error,
}) => {
    const { translate } = useTranslate(CANCEL_CAMPAIGN_MODAL_BUNDLE);

    // Suppress dismiss while the API call is in flight to prevent races
    // (e.g., user closes the modal mid-request and a stale success/error
    // would set state on a hidden modal).
    const handleDismiss = () => {
        if (cancelling) return;
        onDismiss();
    };

    return (
        <Modal
            visible={visible}
            onDismiss={handleDismiss}
            closeAriaLabel={translate('close_aria_label')}
            header={translate('modal_title')}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={handleDismiss} disabled={cancelling}>
                            {translate('go_back_button')}
                        </Button>
                        <Button variant="primary" onClick={onConfirm} loading={cancelling} disabled={cancelling}>
                            {translate('cancel_campaign_button')}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="s">
                {error && <Alert type="error">{error}</Alert>}

                <Box variant="p">
                    {translate('warning_text', { campaignTitle })}
                </Box>

                <Box variant="p">
                    <Box variant="strong" display="inline">
                        {translate('campaign_id_label')}
                        {': '}
                    </Box>
                    {campaignId}
                </Box>
            </SpaceBetween>
        </Modal>
    );
};

export default CancelCampaignModal;
